/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from 'react';

import ButtonsTagsPositions from './ButtonsTagsPositions';

import { useTeamStore } from '@/hooks/useTeamStore';
import { TeamActionTypes } from '@/store/team';
import {
  KeysOf,
  Maybe,
  PlayerPositionAbbr,
  PlayerPositionAbbrF5,
} from '@/types';
import {
  getPositionLabelByPositionId,
  PLAYER_POSITION_BY_ID,
  POSITIONS_LABELS,
} from '@/utils';
import { fieldPlayersF5, goalKepperF5 } from '@/utils/team';

type PositionKey = KeysOf<typeof PLAYER_POSITION_BY_ID>;

type Props = {
  onSubmit?: (pos: Maybe<PlayerPositionAbbr | PlayerPositionAbbr[]>) => void;
  positionByFilter?: Maybe<
    | PlayerPositionAbbrF5
    | PlayerPositionAbbrF5[]
    | PlayerPositionAbbr
    | PlayerPositionAbbr[]
  >;
};

export const ButtonsTagsPositionsF5 = ({
  positionByFilter,
  onSubmit,
}: Props) => {
  const { state, dispatch } = useTeamStore();
  const goalkeeper: PlayerPositionAbbr[] = goalKepperF5;

  // arquero seleccionado
  const selectedPositionIsGoalKeeper = useMemo(() => {
    return state.selectedPosition === POSITIONS_LABELS.abbreviation.ARQ;
  }, [state.selectedPosition]);

  // jugadores de campo
  const selectedPositionIsFieldPlayers = useMemo(() => {
    return state.selectedPosition === POSITIONS_LABELS.all_field_players;
  }, [state.selectedPosition]);

  // seteo estado local inicial
  const [statePosition, setStatePosition] = useState<
    PlayerPositionAbbr | PlayerPositionAbbr[] | null
  >(null);

  // data inicial. Toma los filtros aplicados o las posiciones correspondientes seleccionadas
  const selectedPlayerPosition = useMemo(() => {
    if (positionByFilter) {
      return positionByFilter;
    }

    if (state.filtersApplied?.positionF5) {
      if (state.selectedPlayerOnBuildTeam) {
        return selectedPositionIsGoalKeeper
          ? [state.selectedPosition]
          : typeof state.filtersApplied?.positionF5 === 'string'
          ? fieldPlayersF5
          : state.filtersApplied?.positionF5;
      }
    }

    if (
      state.selectedPosition === statePosition &&
      state.showFiltersPlayersList
    ) {
      return null;
    }

    if (state.selectedPosition && !state.selectedPlayerOnBuildTeam) {
      return state.selectedPosition;
    }

    if (selectedPositionIsGoalKeeper) {
      return state.selectedPlayerOnBuildTeam
        ? goalkeeper
        : state.selectedPosition;
    }

    if (selectedPositionIsFieldPlayers) {
      return fieldPlayersF5;
    }

    return null;
  }, [
    selectedPositionIsGoalKeeper,
    selectedPositionIsFieldPlayers,
    state.selectedPosition,
    positionByFilter,
    state.filtersApplied?.positionF5,
    dispatch,
  ]);

  /* manejo seteo estado local */
  useEffect(() => {
    if (
      selectedPlayerPosition &&
      selectedPlayerPosition !== POSITIONS_LABELS.all_field_players
    ) {
      setStatePosition(selectedPlayerPosition as PlayerPositionAbbr[]);
    }

    return () => {
      setStatePosition(null);
    };
  }, [selectedPlayerPosition]);

  // deshabilito botones segun criterio
  const isDisabledPositionButton = (positionId: PositionKey) => {
    if (state.selectedPlayerOnTeam && selectedPositionIsGoalKeeper) {
      return false;
    }

    if (
      (Array.isArray(selectedPlayerPosition) &&
        state.selectedPlayerOnBuildTeam) ||
      (Array.isArray(selectedPlayerPosition) && state.selectedPlayerOnTeam)
    ) {
      const positionLabel = getPositionLabelByPositionId(positionId);
      return selectedPositionIsGoalKeeper
        ? fieldPlayersF5.includes(positionLabel)
        : goalkeeper.includes(positionLabel);
    } else {
      return false;
    }
  };

  // quito jugador a lista de botones de jugadores de campo
  const deleteItem = (positionLabel: PlayerPositionAbbr) => {
    return (
      Array.isArray(statePosition) &&
      statePosition?.filter((item) => item !== positionLabel)
    );
  };

  // sumo jugador a lista de botones de jugadores de campo
  const addItem = (positionLabel: PlayerPositionAbbr) => {
    return statePosition?.concat(positionLabel);
  };

  // chequeo si estan activos los botones de posiciones
  const handleIsChecked = (positionId: PositionKey): boolean => {
    if (Array.isArray(selectedPlayerPosition)) {
      const positionLabel = PLAYER_POSITION_BY_ID[positionId];
      return Boolean(statePosition?.includes(positionLabel));
    } else {
      return Boolean(
        selectedPlayerPosition &&
          selectedPlayerPosition === PLAYER_POSITION_BY_ID[positionId]
      );
    }
  };

  // seteo de datos para seleccion de jugadores seleccionados desde cancha
  const handleSubmitPosition = (
    positionId: PositionKey,
    positionF5 = false
  ) => {
    const positionLabel = PLAYER_POSITION_BY_ID[positionId];
    const selectedPosition = statePosition?.includes(positionLabel)
      ? deleteItem(positionLabel)
      : addItem(positionLabel);

    if (Array.isArray(selectedPosition)) {
      if (positionF5) {
        dispatch({
          type: TeamActionTypes.SET_STATE,
          payload: {
            filtersApplied: {
              ...state.filtersApplied,
              positionF5: selectedPosition,
            },
          },
        });
      } else {
        onSubmit && onSubmit(selectedPosition);
        setStatePosition(selectedPosition);
      }
    }
  };

  // manejo de onclick
  const handleSelectPositionButton = (positionId: PositionKey) => {
    // retorno si es arquero o no selecciono jugador de cancha. Evito destildar boton seleccionado
    if (state.selectedPlayerOnBuildTeam && selectedPositionIsGoalKeeper) {
      return;
    }

    if (state.selectedPlayerOnBuildTeam) {
      // accion de botones seleccionados desde cancha
      if (!state.showFiltersPlayersList) {
        handleSubmitPosition(positionId, true);
      } else {
        handleSubmitPosition(positionId);
      }
    } else {
      // accion de botones seleccionados desde filtros o listado de jugadores
      const positionLabel = PLAYER_POSITION_BY_ID[positionId];

      const position =
        selectedPlayerPosition === positionLabel ? null : positionLabel;

      if (!state.showFiltersPlayersList) {
        dispatch({
          type: TeamActionTypes.SET_STATE,
          payload: {
            selectedPosition: position,
          },
        });
      } else {
        const selectedPosition =
          state.selectedPosition &&
          state.selectedPosition !== POSITIONS_LABELS.all_field_players
            ? state.selectedPosition
            : null;

        setStatePosition(selectedPosition);
        onSubmit && onSubmit(position);
      }
    }
  };

  return (
    <ButtonsTagsPositions
      handleIsChecked={handleIsChecked}
      handleSelectPositionButton={handleSelectPositionButton}
      isDisabledPositionButton={isDisabledPositionButton}
    />
  );
};
